import { useCallback, useMemo, useState } from 'react';
import { Box, InputBase, Tooltip } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { List, type RowComponentProps } from 'react-window';
import { Dot } from './parts';
import { headBg } from './styles';
import type { ShellJob, ShellJobItem } from '@/lib/shell-job.service';
import {
  ITEM_RESULT,
  RESULT_FILTER_STATUSES,
  answered,
  countFor,
  deviceLabel,
  deviceRowLabel,
  filterChips,
  isNoTimeout,
  itemDuration,
  itemTone,
  type ResultFilter,
} from '@/lib/shell-job-status';
import { axisTicks, compareItems, formatInstant, itemBar, timelineAxis, type Axis } from '@/lib/run-timeline';
import { SHELL_MONO, TONES } from '@/theme/tokens';

const ROW_HEIGHT = 44;
const LIST_MAX_HEIGHT = 560;
const columns = {
  gridTemplateColumns: '210px minmax(0, 1fr) 72px',
  '@media (max-width: 700px)': { gridTemplateColumns: '150px minmax(0, 1fr) 56px' },
} as const;
/** Not sent yet: a thin gray line. */
const queuedSx = { position: 'absolute', top: '50%', height: 4, transform: 'translateY(-50%)', borderRadius: '2px', bgcolor: TONES.neutral.main, opacity: 0.45 } as const;
const slide = keyframes`to { background-position: 11.3px 0; }`;

function AxisHeader({ axis, timezone }: { axis: Axis; timezone: string }) {
  return (
    <Box
      sx={{
        ...columns,
        display: 'grid',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        height: 44,
        bgcolor: headBg,
        borderBottom: 1,
        borderColor: 'divider',
        fontSize: 11,
        fontWeight: 600,
        color: 'text.secondary',
      }}
    >
      <span>Device</span>
      <Box sx={{ position: 'relative', height: '100%' }}>
        {axisTicks(axis, timezone).map((t) => (
          <Box
            key={t.pct}
            sx={{
              position: 'absolute',
              top: 5,
              left: `${t.pct}%`,
              transform: t.pct === 0 ? 'none' : t.pct === 100 ? 'translateX(-100%)' : 'translateX(-50%)',
              textAlign: t.pct === 100 ? 'right' : 'left',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
              fontWeight: 500,
              // Narrower screens keep 0/50/100% only.
              ...(t.pct === 25 || t.pct === 75 ? { '@media (max-width: 1300px)': { display: 'none' } } : {}),
            }}
          >
            <Box component="i" sx={{ display: 'block', minHeight: 13, fontStyle: 'normal', fontWeight: 400, fontSize: 10, color: 'text.disabled' }}>
              {t.date}
            </Box>
            {t.time}
          </Box>
        ))}
      </Box>
      <Box component="span" sx={{ textAlign: 'right' }}>
        Took
      </Box>
    </Box>
  );
}

/**
 * A bar's hover text. A component of its own so MUI renders it, and the times
 * get formatted, only for the row under the pointer: not for every visible
 * row on every clock tick.
 */
function RowTitle({ it, timezone, now, noTimeout }: { it: ShellJobItem; timezone: string; now: number; noTimeout: boolean }) {
  if (!it.started_at) return ITEM_RESULT[it.status].label;
  const sent = `Sent ${formatInstant(it.started_at, timezone, now)}`;
  const reply = answered(it, timezone, now, noTimeout);
  return it.status === 'ok' || it.status === 'exit_nonzero' ? `${sent} · Answered ${reply}` : `${sent} · ${reply}`;
}

interface RowProps {
  getRow: (index: number) => ShellJobItem;
  axis: Axis;
  now: number;
  /** Where now is on the axis, in %: queued and waiting devices are drawn up to it. */
  nowPct: number;
  timezone: string;
  selectedId?: string;
  onSelect: (id: string) => void;
  /** A no-timeout run: sent devices are markers, "Sent OK" is green, late answers say how late. */
  noTimeout: boolean;
}

function TimelineRow({ index, style, ariaAttributes, getRow, axis, now, nowPct, timezone, selectedId, onSelect, noTimeout }: RowComponentProps<RowProps>) {
  const it = getRow(index);
  const bar = itemBar(it, axis, now, noTimeout);
  const tone = itemTone(it, noTimeout);
  const selected = it.id === selectedId;
  return (
    <Box
      style={style}
      {...ariaAttributes}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={() => onSelect(it.id)}
      onKeyDown={(e) => {
        // Space would also scroll the list.
        if (e.key === ' ') e.preventDefault();
        if (e.key === 'Enter' || e.key === ' ') onSelect(it.id);
      }}
      sx={{
        ...columns,
        display: 'grid',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        cursor: 'pointer',
        borderBottom: 1,
        borderColor: 'divider',
        ...(selected
          ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.1), boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}` }
          : { '&:hover': { bgcolor: 'action.hover' } }),
      }}
    >
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Dot tone={tone} />
        <Box sx={{ minWidth: 0, '& > div': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
          <Box sx={{ fontFamily: SHELL_MONO, fontSize: 12 }}>{deviceLabel(it)}</Box>
          <Box sx={{ fontSize: 11, color: 'text.secondary' }} title={deviceRowLabel(it)}>
            {deviceRowLabel(it)}
          </Box>
        </Box>
      </Box>
      <Tooltip title={<RowTitle it={it} timezone={timezone} now={now} noTimeout={noTimeout} />} placement="top">
        <Box
          sx={{
            position: 'relative',
            alignSelf: 'stretch',
            // Grid lines at the 25% ticks.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: (t) => `linear-gradient(to right, ${t.palette.divider} 1px, transparent 1px)`,
              backgroundSize: '25% 100%',
              opacity: 0.6,
            },
          }}
        >
          {/* Geometry goes in style, not sx: it changes every tick, and emotion keeps a class for every distinct value. */}
          {bar.kind === 'waiting' && (
            <Box
              sx={{ position: 'absolute', top: '50%', height: 10, transform: 'translateY(-50%)', border: '1.5px dashed', borderColor: TONES.neutral.main, borderRadius: '2px' }}
              style={{ left: 0, width: `${nowPct}%` }}
            />
          )}
          {bar.kind === 'none' && it.status === 'pending' && <Box sx={queuedSx} style={{ left: 0, width: `${nowPct}%` }} />}
          {bar.kind === 'sent' && (
            <>
              <Box sx={queuedSx} style={{ left: 0, width: `${bar.sentPct}%` }} />
              {/* Sent, no answer yet: a hollow diamond at the send time. */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: '50%',
                  width: 9,
                  height: 9,
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  border: '2px solid',
                  borderColor: TONES.success.main,
                  bgcolor: 'background.paper',
                  borderRadius: '2px',
                }}
                style={{ left: `${bar.sentPct}%` }}
              />
            </>
          )}
          {(bar.kind === 'done' || bar.kind === 'running') && (
            <>
              {!bar.late && <Box sx={queuedSx} style={{ left: 0, width: `${bar.sentPct}%` }} />}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  height: 10,
                  minWidth: 3,
                  transform: 'translateY(-50%)',
                  borderRadius: '2px',
                  bgcolor: TONES[tone].main,
                  ...(bar.kind === 'running' && {
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0 4px, transparent 4px 8px)',
                    backgroundSize: '11.3px 11.3px',
                    animation: `${slide} 1s linear infinite`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }),
                }}
                style={{ left: `${bar.sentPct}%`, width: `${bar.endPct - bar.sentPct}%` }}
              />
              {bar.lateAnswer && (
                <Box
                  component="span"
                  sx={{ position: 'absolute', top: '50%', right: 4, transform: 'translateY(-50%)', fontSize: 10, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}
                >
                  {bar.lateAnswer}
                </Box>
              )}
              {bar.late && (
                <Box
                  component="span"
                  sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  style={{ right: `${100 - bar.sentPct + 1}%` }}
                >
                  {bar.late}
                </Box>
              )}
            </>
          )}
        </Box>
      </Tooltip>
      <Box sx={{ textAlign: 'right', fontFamily: SHELL_MONO, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {noTimeout && it.status === 'sent' ? '—' : itemDuration(it)}
      </Box>
    </Box>
  );
}

interface DeviceTimelineProps {
  job: ShellJob;
  items: ShellJobItem[];
  now: number;
  timezone: string;
  selectedId?: string;
  onSelect: (id: string) => void;
}

/** Filter chips, search, the time axis and one virtualized row per device. The left half of the run page's panel. */
export function DeviceTimeline({ job, items, now, timezone, selectedId, onSelect }: DeviceTimelineProps) {
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [search, setSearch] = useState('');
  const noTimeout = isNoTimeout(job);
  const axis = useMemo(() => timelineAxis(job, items, now), [job, items, now]);
  const nowPct = Math.min(100, Math.max(0, ((now - axis.start) / (axis.end - axis.start)) * 100));
  // Sorted once per items change, never on the clock tick; filtering keeps the
  // order, so a chip or a keystroke does not re-sort the whole run.
  const sorted = useMemo(() => [...items].sort(compareItems), [items]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statuses = filter === 'all' ? null : RESULT_FILTER_STATUSES[filter];
    return sorted
      .filter((i) => !statuses || statuses.includes(i.status))
      .filter((i) => !q || i.machine_id.toLowerCase().includes(q));
  }, [sorted, filter, search]);
  // Rows get a getter, not the array: React 19's dev profiler diffs every row's props,
  // and a large array there runs the tab out of memory (see the note in log-viewer.tsx).
  const getRow = useCallback((i: number) => rows[i], [rows]);

  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {filterChips(noTimeout).map((c) => {
            const on = filter === c.key;
            return (
              <Box
                key={c.key}
                component="button"
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(c.key)}
                sx={{
                  px: '10px',
                  py: '4px',
                  border: 1,
                  borderRadius: '20px',
                  font: 'inherit',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  ...(on
                    ? { borderColor: 'text.primary', bgcolor: 'text.primary', color: 'background.paper' }
                    : { borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }),
                }}
              >
                {c.label}
                <Box component="span" sx={{ ml: 0.5, ...(on ? { opacity: 0.7 } : { color: 'text.secondary' }) }}>
                  {countFor(job.counts, c.key).toLocaleString()}
                </Box>
              </Box>
            );
          })}
        </Box>
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search machine ID"
          inputProps={{ 'aria-label': 'Search machine ID' }}
          sx={{
            ml: 'auto',
            width: { xs: '100%', sm: 240 },
            px: '10px',
            py: '6px',
            fontSize: 13,
            border: 1,
            borderColor: 'divider',
            borderRadius: '6px',
            bgcolor: 'background.paper',
            '& input': { p: 0 },
            '&.Mui-focused': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-1px' },
          }}
        />
      </Box>
      <AxisHeader axis={axis} timezone={timezone} />
      {rows.length === 0 ? (
        <Box sx={{ px: 1.5, py: 1.25, fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>No devices match.</Box>
      ) : (
        <>
          <List
            role="listbox"
            aria-label="Devices"
            rowComponent={TimelineRow}
            rowCount={rows.length}
            rowHeight={ROW_HEIGHT}
            rowProps={{ getRow, axis, now, nowPct, timezone, selectedId, onSelect, noTimeout }}
            overscanCount={10}
            style={{ height: Math.min(rows.length * ROW_HEIGHT, LIST_MAX_HEIGHT) }}
          />
          <Box sx={{ px: 1.5, py: 1.25, fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
            Showing {rows.length.toLocaleString()} of {job.counts.total.toLocaleString()}
            {rows.length * ROW_HEIGHT > LIST_MAX_HEIGHT && ' · scroll for more'}
          </Box>
        </>
      )}
    </Box>
  );
}
